<template>
  <ParticleAnim class="about-section-wrapper" :class="{ 'theme--light': !$vuetify.theme.dark }">
    <v-container class="about-section" fluid>
    <!-- Hero Section -->
    <v-row class="hero-section" align="center" justify="center">
      <v-col cols="12" md="10" lg="8">
        <div class="greeting-container">
          <h1 class="greeting">{{ about.greeting }}</h1>
          <div class="greeting-underline"></div>
        </div>

        <p class="introduction">
          {{ about.introduction }}
        </p>

        <div class="cta-buttons">
          <v-btn color="primary" large rounded class="download-cv-btn" @click="downloadCV">
            <v-icon left>mdi-download</v-icon>
            Download CV
          </v-btn>

          <v-btn outlined large rounded class="view-projects-btn" @click="viewProjects">
            <v-icon left>mdi-briefcase-outline</v-icon>
            View Projects
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Skills Section Component -->
    <SkillsSection
      :skills="skills"
      :description="about.skills.description"
    />

    <!-- Career Highlights Section Component -->
    <CareerHighlightsSection
      :highlights="careerHighlights"
      :description="about.careerHighlights.description"
    />

    <!-- Featured Projects Section Component -->
    <FeaturedProjectsSection
      :projects="projects"
      :description="about.collaboration.description"
      @viewAllProjects="viewAllProjects"
      @showProjectDetails="showProjectDetails"
    />

    <!-- Values Section -->
    <section class="values-section enhanced-section">
      <div class="section-container">
        <v-card class="values-card enhanced-card" elevation="0" :class="{ 'theme--dark': $vuetify.theme.dark }">
          <v-card-text class="values-content">
            <h2 class="values-title enhanced-title">My Values</h2>
            <p class="values-description">{{ about.values.description }}</p>

            <div class="values-icons">
              <div class="value-item enhanced-value-item">
                <v-avatar size="60" :color="$vuetify.theme.dark ? 'primary darken-3' : 'primary lighten-4'" class="value-icon">
                  <v-icon :color="$vuetify.theme.dark ? 'primary lighten-3' : 'primary'" size="32">mdi-school</v-icon>
                </v-avatar>
                <div class="value-label">Lifelong Learning</div>
              </div>

              <div class="value-item enhanced-value-item">
                <v-avatar size="60" :color="$vuetify.theme.dark ? 'success darken-3' : 'success lighten-4'" class="value-icon">
                  <v-icon :color="$vuetify.theme.dark ? 'success lighten-3' : 'success'" size="32">mdi-handshake</v-icon>
                </v-avatar>
                <div class="value-label">Collaboration</div>
              </div>

              <div class="value-item enhanced-value-item">
                <v-avatar size="60" :color="$vuetify.theme.dark ? 'info darken-3' : 'info lighten-4'" class="value-icon">
                  <v-icon :color="$vuetify.theme.dark ? 'info lighten-3' : 'info'" size="32">mdi-account-heart</v-icon>
                </v-avatar>
                <div class="value-label">User Experience</div>
              </div>

              <div class="value-item enhanced-value-item">
                <v-avatar size="60" :color="$vuetify.theme.dark ? 'warning darken-3' : 'warning lighten-4'" class="value-icon">
                  <v-icon :color="$vuetify.theme.dark ? 'warning lighten-3' : 'warning'" size="32">mdi-shield-check</v-icon>
                </v-avatar>
                <div class="value-label">Integrity</div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </section>

    <!-- Spacer for footer -->
    <div class="footer-spacer"></div>
    </v-container>
  </ParticleAnim>
</template>

<script>
import aboutData from '@/assets/about.json';
import ParticleAnim from '@/components/ParticleAnim.vue';
import SkillsSection from '@/components/subcomponents/SkillsSection.vue';
import CareerHighlightsSection from '@/components/subcomponents/CareerHighlightsSection.vue';
import FeaturedProjectsSection from '@/components/subcomponents/FeaturedProjectsSection.vue';

export default {
  name: 'About',
  components: {
    ParticleAnim,
    SkillsSection,
    CareerHighlightsSection,
    FeaturedProjectsSection
  },
  data() {
    return {
      about: aboutData.about,
      skills: [
        {
          title: 'Python',
          description: aboutData.about.skills.languages.python,
          icon: 'mdi-language-python',
          color: '#3572A5',
          level: 85,
          isHovered: false
        },
        {
          title: 'JavaScript',
          description: aboutData.about.skills.languages.javascript,
          icon: 'mdi-language-javascript',
          color: '#F0DB4F',
          level: 90,
          isHovered: false
        },
        {
          title: 'C++',
          description: aboutData.about.skills.languages.cplusplus,
          icon: 'mdi-language-cpp',
          color: '#00599C',
          level: 70,
          isHovered: false
        },
        {
          title: 'C#',
          description: aboutData.about.skills.languages.csharp,
          icon: 'mdi-language-csharp',
          color: '#68217A',
          level: 75,
          isHovered: false
        },
        {
          title: 'Java',
          description: aboutData.about.skills.languages.java,
          icon: 'mdi-language-java',
          color: '#007396',
          level: 85,
          isHovered: false
        },
        {
          title: 'Vue.js',
          description: aboutData.about.skills.frontEndFrameworks.vue,
          icon: 'mdi-vuejs',
          color: '#42B883',
          level: 95,
          isHovered: false
        },
        {
          title: 'React',
          description: aboutData.about.skills.frontEndFrameworks.react,
          icon: 'mdi-react',
          color: '#61DAFB',
          level: 70,
          isHovered: false
        },
        {
          title: 'Angular',
          description: aboutData.about.skills.frontEndFrameworks.angular,
          icon: 'mdi-angular',
          color: '#E23237',
          level: 70,
          isHovered: false
        },
        {
          title: 'Flask',
          description: aboutData.about.skills.backEndDevelopment.flask,
          icon: 'mdi-flask',
          color: '#3F4F75',
          level: 85,
          isHovered: false
        },
        {
          title: 'RESTful API',
          description: aboutData.about.skills.backEndDevelopment.restApi,
          icon: 'mdi-api',
          color: '#66BB6A',
          level: 75,
          isHovered: false
        },
      ],
      careerHighlights: [
        {
          img: require('@/assets/webdes.png'),
          title: 'Senior Frontend Developer',
          description: 'Led a team of developers to create responsive, high-performance web applications that increased user engagement by 40%.',
          date: '2022 - Present',
          metrics: [
            { icon: 'mdi-trending-up', color: '#4caf50', text: '40% increase in user engagement' },
            { icon: 'mdi-account-group', color: '#2196f3', text: 'Led team of 5 developers' },
            { icon: 'mdi-rocket-launch', color: '#ff9800', text: '15+ successful deployments' }
          ],
          skills: ['Vue.js', 'React', 'TypeScript', 'Leadership', 'Agile']
        },
        {
          img: require('@/assets/webdes.png'),
          title: 'Full Stack Developer',
          description: 'Developed and maintained full-stack applications using Vue.js, Flask, and RESTful APIs, improving system efficiency by 30%.',
          date: '2020 - 2022',
          metrics: [
            { icon: 'mdi-speedometer', color: '#4caf50', text: '30% system efficiency improvement' },
            { icon: 'mdi-api', color: '#9c27b0', text: '25+ RESTful APIs developed' },
            { icon: 'mdi-database', color: '#607d8b', text: 'Database optimization expertise' }
          ],
          skills: ['Python', 'Flask', 'Vue.js', 'PostgreSQL', 'Docker']
        },
        {
          img: require('@/assets/webdes.png'),
          title: 'Web Development Intern',
          description: 'Gained hands-on experience in web development, collaborating with senior developers and learning industry best practices.',
          date: '2019 - 2020',
          metrics: [
            { icon: 'mdi-school', color: '#3f51b5', text: 'Rapid skill acquisition' },
            { icon: 'mdi-code-tags', color: '#e91e63', text: '10+ projects completed' },
            { icon: 'mdi-account-tie', color: '#795548', text: 'Mentored by senior developers' }
          ],
          skills: ['HTML', 'CSS', 'JavaScript', 'Git', 'Problem Solving']
        },
      ],
      projects: [
        {
          name: 'Telegram Bot Integration',
          img: require('@/assets/projects/tgbot.jpg'),
          description: 'Created a Telegram bot that automates notifications and user interactions for streamlined communication.',
          shortDescription: 'AI-powered Telegram bot for automated communication',
          link: 'https://t.me/facecheckV1_bot',
          github: '',
          status: 'Live',
          technologies: ['Node.js', 'Telegram API', 'MongoDB', 'Express'],
          features: [
            'Real-time notifications',
            'User interaction automation',
            'Multi-language support',
            'Analytics dashboard'
          ],
          metrics: [
            { icon: 'mdi-account-group', color: '#4caf50', text: '1000+ active users' },
            { icon: 'mdi-message-processing', color: '#2196f3', text: '10K+ messages processed daily' },
            { icon: 'mdi-clock-fast', color: '#ff9800', text: '99.9% uptime' }
          ]
        },
        {
          name: 'MLMC Investment Platform',
          img: require('@/assets/projects/inves.png'),
          description: 'Developed an investment platform that enables users to track their portfolios and manage investments with real-time data.',
          shortDescription: 'Modern investment tracking and portfolio management platform',
          link: 'https://mlmc-vip.com',
          github: '',
          status: 'Live',
          technologies: ['Vue.js', 'Vuetify', 'Chart.js', 'Firebase', 'Node.js'],
          features: [
            'Real-time portfolio tracking',
            'Advanced analytics',
            'Multi-asset support',
            'Risk assessment tools'
          ],
          metrics: [
            { icon: 'mdi-chart-line', color: '#4caf50', text: '$2M+ assets tracked' },
            { icon: 'mdi-account-multiple', color: '#2196f3', text: '500+ investors' },
            { icon: 'mdi-trending-up', color: '#ff9800', text: '15% average returns' }
          ]
        },
        {
          name: 'Coin App - Crypto Exchange',
          img: require('@/assets/projects/coinapp.jpg'),
          description: 'Built a cryptocurrency exchange platform with features like secure trading, wallet management, and real-time market analysis.',
          shortDescription: 'Secure cryptocurrency exchange with advanced trading features',
          link: '',
          github: '',
          status: 'In Development',
          technologies: ['Vue.js', 'Node.js', 'Express', 'MongoDB', 'WebSocket'],
          features: [
            'Secure crypto trading',
            'Multi-wallet support',
            'Real-time market data',
            'Advanced charting tools'
          ],
          metrics: [
            { icon: 'mdi-security', color: '#4caf50', text: 'Bank-level security' },
            { icon: 'mdi-currency-btc', color: '#ff9800', text: '50+ cryptocurrencies' },
            { icon: 'mdi-speedometer', color: '#2196f3', text: 'Sub-second execution' }
          ]
        },
      ],
    };
  },
  methods: {
    openLink(url) {
      if (url) {
        window.open(url, "_blank");
      }
    },

    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'live':
          return 'success';
        case 'in development':
          return 'warning';
        case 'planned':
          return 'info';
        default:
          return 'grey';
      }
    },

    downloadCV() {
      // In a real application, this would download the CV file
      this.$store.commit('setSnackbar', {
        show: true,
        text: 'CV download started!',
        color: 'success'
      });
    },

    viewProjects() {
      // Navigate to projects section
      this.$emit('navigate', 'blog');
    },

    viewAllProjects() {
      // Navigate to projects page
      this.$emit('navigate', 'blog');
    },

    showProjectDetails(project) {
      // Show project details in a modal or navigate to project detail page
      console.log('Show project details for:', project.name);
      // You can implement a modal here or navigate to a detailed project page
      this.$store.commit('setSnackbar', {
        show: true,
        text: `Viewing details for ${project.name}`,
        color: 'info'
      });
    }
  },
  mounted() {
    // Initialize AOS
    if (window.AOS) {
      window.AOS.refresh();
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

/* Theme-aware background wrapper - overrides particles */
.about-section-wrapper {
  position: relative;
  overflow: hidden;
  min-height: 100vh;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      135deg,
      #0a0a0a 0%,
      #1a1a2e 50%,
      #16213e 100%
    );
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(72, 149, 239, 0.05));
    z-index: 1;
  }

  /* Light theme styling */
  &.theme--light {
    &::before {
      background: linear-gradient(
        135deg,
        #f8f9fa 0%,
        #e9ecef 50%,
        #dee2e6 100%
      );
    }

    &::after {
      background: linear-gradient(135deg, rgba(67, 97, 238, 0.03), rgba(72, 149, 239, 0.03));
    }
  }

  .v-container {
    position: relative;
    z-index: 2;
  }
}

// General section styling
.about-section {
  padding: 2rem 1rem;
  position: relative;
  z-index: 1;

  @media (min-width: $breakpoint-sm) {
    padding: 3rem 2rem;
  }
}

section {
  margin-bottom: 5rem;
}

.section-header {
  text-align: center;
  margin-bottom: 3rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 2px 10px rgba(67, 97, 238, 0.3);

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
    text-shadow: none;
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 2rem;
  }
}

.section-divider {
  height: 4px;
  width: 80px;
  background-color: var(--v-primary-base);
  margin: 0 auto 1.5rem;
  border-radius: 2px;
}

.section-description {
  max-width: 800px;
  margin: 0 auto;
  font-size: 1.1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.8);

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 1rem;
  }
}

// Hero section
.hero-section {
  text-align: center;
  padding: 2rem 0 4rem;
}

.greeting-container {
  margin-bottom: 2rem;
}

.greeting {
  font-size: 3.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, $primary, $accent);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 4px 20px rgba(67, 97, 238, 0.3);

  @media (max-width: $breakpoint-xs) {
    font-size: 2.5rem;
  }
}

.greeting-underline {
  height: 6px;
  width: 100px;
  background-color: var(--v-primary-base);
  margin: 0 auto;
  border-radius: 3px;
}

.introduction {
  font-size: 1.25rem;
  line-height: 1.8;
  margin-bottom: 2.5rem;
  color: rgba(255, 255, 255, 0.9);

  .theme--light & {
    color: rgba(33, 37, 41, 0.8);
  }

  @media (max-width: $breakpoint-xs) {
    font-size: 1.1rem;
    line-height: 1.6;
  }
}

.cta-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;

  @media (max-width: $breakpoint-xs) {
    flex-direction: column;
    align-items: center;
  }
}

.download-cv-btn,
.view-projects-btn {
  min-width: 180px;
  height: 50px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
  transition: all $transition-medium;

  &:hover {
    transform: translateY(-5px);
    box-shadow: $shadow-medium;
  }

  @media (max-width: $breakpoint-xs) {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}







// Values section
.values-section {
  margin-top: 5rem;
}

.values-card {
  border-radius: 16px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05) !important;
  backdrop-filter: blur(15px) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  transition: all 0.3s ease;

  .theme--light & {
    background: rgba(255, 255, 255, 0.8) !important;
    border-color: rgba(67, 97, 238, 0.15) !important;
  }

  &.theme--dark {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
}

.values-content {
  padding: 3rem 2rem;
  text-align: center;
}

.values-title {
  font-size: clamp(1.8rem, 5vw, 2.2rem);
  font-weight: 700;
  color: var(--v-textPrimary-base);
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 3px;
    background-color: var(--v-primary-base);
    animation: expandWidth 1s ease-out forwards 0.5s;
  }

  .theme--dark & {
    color: white;
  }
}

.values-divider {
  height: 4px;
  width: 60px;
  background-color: var(--v-primary-base);
  margin: 0 auto 1.5rem;
  border-radius: 2px;
  animation: expandWidth 1s ease-out;
}

.values-description {
  max-width: 800px;
  margin: 0 auto 3rem;
  font-size: clamp(1rem, 2vw, 1.1rem);
  line-height: 1.6;
  color: var(--v-textSecondary-base);

  .theme--dark & {
    color: rgba(255, 255, 255, 0.8);
  }
}

.values-icons {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: clamp(1.5rem, 5vw, 3rem);
}

.value-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);

    .value-icon {
      transform: scale(1.1) rotate(10deg);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    }
  }
}

.value-icon {
  transition: all $transition-medium;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.value-label {
  font-weight: 600;
  font-size: clamp(0.9rem, 2vw, 1rem);
  color: var(--v-textPrimary-base);

  .theme--dark & {
    color: white;
  }
}

@keyframes expandWidth {
  from { width: 0; }
  to { width: 60px; }
}

// Footer spacer
.footer-spacer {
  height: 80px;
}

// Animations
@keyframes progressAnimation {
  from {
    width: 0;
  }

  to {
    width: var(--target-width);
  }
}

// Responsive adjustments
@media (max-width: $breakpoint-xs) {
  section {
    margin-bottom: 3rem;
    padding: 0 0.5rem;
  }

  .hero-section {
    padding-top: 2rem;
    padding-bottom: 2rem;
  }

  .greeting {
    font-size: 2rem;
  }

  .introduction {
    font-size: 1rem;
    padding: 0 0.5rem;
  }

  .cta-buttons {
    flex-direction: column;
    gap: 1rem;

    .v-btn {
      width: 100%;
    }
  }

  .section-title {
    font-size: 1.8rem;
  }

  .section-description {
    font-size: 0.9rem;
    padding: 0 0.5rem;
  }

  .skills-grid {
    margin: 0;
  }

  .skill-card {
    margin-bottom: 1rem;
  }

  .values-content {
    padding: 2rem 1rem;
  }

  .values-icons {
    gap: 1rem;
  }

  .value-item {
    width: 40%;
  }

  .values-title {
    font-size: 1.8rem;
  }

  .values-description {
    font-size: 0.9rem;
    margin-bottom: 2rem;
  }

  .value-icon {
    width: 50px !important;
    height: 50px !important;
  }

  .value-label {
    font-size: 0.9rem;
  }

  .project-card {
    margin-bottom: 1rem;
  }

  .highlight-card {
    margin-bottom: 1rem;
  }
}

// Medium screens
@media (min-width: $breakpoint-xs) and (max-width: $breakpoint-md) {
  .cta-buttons {
    justify-content: center;
  }

  .values-content {
    padding: 2.5rem 1.5rem;
  }

  .values-icons {
    gap: 2rem;
  }

  .value-item {
    width: 30%;
  }
}
</style>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.about-section {
  padding: 2rem max(1rem, 5vw);
  overflow-x: hidden;
}

.skill-card {
  height: 100%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
}

.skill-title {
  display: flex;
  align-items: center;
  padding: clamp(0.5rem, 2vw, 1rem);

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
}

.skill-name {
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  margin: 0;
}

.skillico {
  transition: transform 0.3s ease;

  &:hover {
    transform: rotate(360deg);
  }
}

.progress-container {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  overflow: hidden;
  height: clamp(20px, 4vw, 24px);

  .theme--light & {
    background: rgba(67, 97, 238, 0.08);
    border-color: rgba(67, 97, 238, 0.1);
  }
}

.progress-bar {
  width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  position: relative;
  transition: width 1.5s cubic-bezier(0.4, 0, 0.2, 1);

  &.animate-progress {
    animation: progressAnimation 1.5s forwards;
  }
}

.progress-text {
  font-size: clamp(0.75rem, 2vw, 0.875rem);
  white-space: nowrap;
  position: absolute;
  width: 100%;
  text-align: center;
}

.image-container {
  height: clamp(200px, 30vw, 250px);
  background-size: cover;
  background-position: center;
  transition: transform 0.3s ease;
  border-radius: 8px;

  &:hover {
    transform: scale(1.05);
  }
}

.highlight-card,
.project-card {
  transition: all 0.3s ease;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
}

.clickable {
  cursor: pointer;

  &:hover {
    transform: translateY(-5px) scale(1.02);
  }
}

@keyframes progressAnimation {
  from {
    width: 0;
  }

  to {
    width: var(--target-width);
  }
}

.animate__animated {
  animation-duration: 1s;
  animation-fill-mode: both;
}

.animate__fadeIn {
  animation-name: fadeIn;
}

.animate__slideInLeft {
  animation-name: slideInLeft;
}

.animate__fadeInUp {
  animation-name: fadeInUp;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideInLeft {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

h2 {
  font-size: clamp(1.5rem, 4vw, 2rem);
  margin-bottom: clamp(1rem, 3vw, 2rem);
}

.des {
  font-size: clamp(0.875rem, 2vw, 1rem);
  line-height: 1.6;
}

@media (max-width: 600px) {
  .about-section {
    padding: 1rem;
  }

  .skill-card {
    margin-bottom: 1rem;
  }

  .progress-container {
    margin: 0.5rem 0;
  }
}

@media (min-width: 601px) and (max-width: 960px) {
  .about-section {
    padding: 1.5rem;
  }

  .skill-card {
    margin-bottom: 1.5rem;
  }

  .image-container {
    height: 220px;
  }

  .highlight-card,
  .project-card {
    margin-bottom: 1.5rem;
  }
}

@media (min-width: 961px) {
  .about-section {
    padding: 2rem;
  }

  .skill-card {
    margin-bottom: 2rem;
  }

  .image-container {
    height: 250px;
  }

  .highlight-card,
  .project-card {
    margin-bottom: 2rem;
  }
}

.spcr {
  height: 50px;
}

// Extra Responsive Fixes
@media (max-width: 480px) {
  .skill-title {
    padding: 0.5rem;
  }

  .skillico {
    margin-right: 0 !important;
  }

  .progress-container {
    height: 18px;
  }

  .image-container {
    height: 180px;
  }

  .des {
    font-size: 0.875rem;
  }
}

// Print styles
@media print {
  .about-section {
    padding: 0;
  }

  .animate__animated {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
  }

  .image-container {
    break-inside: avoid;
  }

  .highlight-card,
  .project-card,
  .skill-card {
    break-inside: avoid;
    page-break-inside: avoid;
    box-shadow: none !important;
    border: 1px solid #ddd !important;
  }
}

// Enhanced Glassmorphism Styles - Footer-like UI
.enhanced-section {
  position: relative;
  margin-bottom: 6rem;

  .section-container {
    background: linear-gradient(
      135deg,
      rgba(10, 10, 10, 0.4) 0%,
      rgba(26, 26, 46, 0.4) 50%,
      rgba(22, 33, 62, 0.4) 100%
    );
    backdrop-filter: blur(20px);
    border: 1px solid rgba(67, 97, 238, 0.15);
    border-radius: 24px;
    padding: 3rem 2rem;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(67, 97, 238, 0.08), rgba(72, 149, 239, 0.08));
      z-index: 0;
    }

    .theme--light & {
      background: linear-gradient(
        135deg,
        rgba(248, 249, 250, 0.9) 0%,
        rgba(240, 242, 247, 0.9) 50%,
        rgba(233, 236, 239, 0.9) 100%
      );
      border-color: rgba(67, 97, 238, 0.2);

      &::before {
        background: linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(72, 149, 239, 0.05));
      }
    }

    > * {
      position: relative;
      z-index: 1;
    }
  }

  @media (max-width: 768px) {
    .section-container {
      padding: 2rem 1.5rem;
      border-radius: 16px;
    }
  }
}

.enhanced-title {
  font-size: 2.5rem !important;
  font-weight: 700 !important;
  color: #ffffff !important;
  margin-bottom: 2rem !important;
  position: relative;
  text-shadow: 0 2px 10px rgba(67, 97, 238, 0.3);
  text-align: center;

  .theme--light & {
    color: rgba(33, 37, 41, 0.9) !important;
    text-shadow: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #4361ee, #4895ef);
    border-radius: 2px;
    box-shadow: 0 2px 10px rgba(67, 97, 238, 0.3);
  }

  @media (max-width: 768px) {
    font-size: 2rem !important;
  }
}

.enhanced-card {
  background: rgba(255, 255, 255, 0.05) !important;
  backdrop-filter: blur(15px) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 16px !important;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
  height: 100%;
  position: relative;
  overflow: hidden;

  .theme--light & {
    background: rgba(255, 255, 255, 0.8) !important;
    border-color: rgba(67, 97, 238, 0.15) !important;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(67, 97, 238, 0.5), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02) !important;
    background: rgba(67, 97, 238, 0.1) !important;
    border-color: rgba(67, 97, 238, 0.3) !important;
    box-shadow: 0 20px 40px rgba(67, 97, 238, 0.2) !important;

    .theme--light & {
      background: rgba(67, 97, 238, 0.08) !important;
      box-shadow: 0 20px 40px rgba(67, 97, 238, 0.15) !important;
    }

    &::before {
      opacity: 1;
    }
  }
}

.enhanced-btn {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 2px solid rgba(67, 97, 238, 0.3) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 16px !important;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
  min-width: 200px !important;
  height: 56px !important;

  .theme--light & {
    background: rgba(255, 255, 255, 0.9) !important;
    border-color: rgba(67, 97, 238, 0.4) !important;
  }

  &:hover {
    transform: translateY(-4px) !important;
    background: rgba(67, 97, 238, 0.2) !important;
    border-color: rgba(67, 97, 238, 0.5) !important;
    box-shadow: 0 15px 30px rgba(67, 97, 238, 0.3) !important;

    .theme--light & {
      background: rgba(67, 97, 238, 0.1) !important;
      box-shadow: 0 15px 30px rgba(67, 97, 238, 0.2) !important;
    }
  }
}

.enhanced-value-item {
  background: rgba(255, 255, 255, 0.05) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 16px !important;
  padding: 1.5rem !important;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1) !important;
  backdrop-filter: blur(10px) !important;

  .theme--light & {
    background: rgba(255, 255, 255, 0.7) !important;
    border-color: rgba(67, 97, 238, 0.1) !important;
  }

  &:hover {
    transform: translateY(-6px) scale(1.05) !important;
    background: rgba(67, 97, 238, 0.15) !important;
    border-color: rgba(67, 97, 238, 0.3) !important;
    box-shadow: 0 15px 30px rgba(67, 97, 238, 0.2) !important;

    .theme--light & {
      background: rgba(67, 97, 238, 0.08) !important;
      box-shadow: 0 15px 30px rgba(67, 97, 238, 0.15) !important;
    }

    .value-icon {
      transform: rotate(10deg) scale(1.1) !important;
    }

    .value-label {
      color: rgba(255, 255, 255, 1) !important;

      .theme--light & {
        color: #4361ee !important;
      }
    }
  }
}

// Enhanced Section Descriptions
.section-description {
  max-width: 700px !important;
  margin: 0 auto 3rem !important;
  font-size: 1.1rem !important;
  line-height: 1.7 !important;
  color: rgba(255, 255, 255, 0.85) !important;
  text-align: center;
  background: rgba(255, 255, 255, 0.03);
  padding: 1.5rem 2rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);

  .theme--light & {
    color: rgba(73, 80, 87, 0.85) !important;
    background: rgba(255, 255, 255, 0.6);
    border-color: rgba(67, 97, 238, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 1rem !important;
    padding: 1.25rem 1.5rem;
  }
}

// Enhanced spacing and animations
.skills-grid,
.career-section .v-row,
.projects-section .v-row {
  margin-top: 3rem !important;
}

.view-all-projects {
  text-align: center;
  margin-top: 4rem;
}

// Value items grid enhancement
.values-icons {
  display: grid !important;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)) !important;
  gap: 2rem !important;
  margin-top: 3rem !important;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)) !important;
    gap: 1.5rem !important;
  }
}

// Enhanced tech chips in project cards
.tech-chip {
  background: rgba(67, 97, 238, 0.2) !important;
  border: 1px solid rgba(67, 97, 238, 0.3) !important;
  color: rgba(255, 255, 255, 0.9) !important;

  .theme--light & {
    background: rgba(67, 97, 238, 0.1) !important;
    color: rgba(67, 97, 238, 0.9) !important;
  }
}

// Enhanced status chips
.status-chip {
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}
</style>